import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VendorContactFormField } from 'contacts/elements';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { ProjectFormField } from 'projects/components';
import { QuoteEntityType } from 'quotes/enums';

import { IVendorQuoteCreateForm } from './types';

export type TVendorQuoteUpsertDisabledFields = {
  project?: boolean;
  jobName?: boolean;
  contact?: boolean;
};

export type TVendorQuoteUpsertHiddenFields = {
  project?: boolean;
  jobName?: boolean;
  externalProjectName?: boolean;
  externalNumber?: boolean;
  revisionNumber?: boolean;
};

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IVendorQuoteCreateForm>) => void;
  onSubmit?: () => (data: IVendorQuoteCreateForm) => void;
  disabledFields?: TVendorQuoteUpsertDisabledFields;
  hiddenFields?: TVendorQuoteUpsertHiddenFields;
  entityType?: QuoteEntityType;
  extraFields?: JSX.Element;
  withoutForm?: boolean;
  withoutNotification?: boolean;
}

export const VendorQuoteUpsertForm = ({
  isOpen,
  onClose,
  onSubmit,
  disabledFields = {},
  hiddenFields = {
    project: false,
    externalProjectName: true,
    externalNumber: true,
    revisionNumber: true,
  },
  entityType = QuoteEntityType.QUOTE,
  extraFields = <></>,
  withoutForm = false,
  withoutNotification = false,
}: IProps) => {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const fieldsStack = (
    <>
      {!hiddenFields.project && <ProjectFormField disabled={disabledFields?.project} />}

      <FormField
        name="jobName"
        type={InputType.TEXT_INPUT}
        hidden={hiddenFields?.jobName}
        inputProps={{
          label: t('labelJobName'),
          disabled: disabledFields.jobName,
          required: true,
        }}
      />
      <VendorContactFormField
        name="contact"
        label={t('labelVendorQuoteContact', { entityType })}
        getLabel={(option: ICorporateUserAutocomplete) => `${option.name} (${option.companyName})`}
        required
        disabled={disabledFields.contact}
      />
      <FormField
        name="externalProjectName"
        type={InputType.TEXT_INPUT}
        hidden={hiddenFields?.externalProjectName}
        inputProps={{
          label: t('labelVendorProjectName'),
        }}
      />
      <FormField
        name="externalNumber"
        type={InputType.TEXT_INPUT}
        hidden={hiddenFields?.externalNumber}
        inputProps={{
          label: t('labelVendorQuoteNumber'),
        }}
      />
      <FormField
        name="revisionNumber"
        type={InputType.TEXT_INPUT}
        hidden={hiddenFields?.revisionNumber}
        inputProps={{
          label: t('labelVendorQuoteRevision'),
        }}
      />

      {QuoteEntityType.ORDER === entityType && (
        <>
          <FormField
            name="purchaseOrderNumber"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelPurchaseOrderNumber'),
            }}
          />
          <FormField
            name="agreeTermsAndConditions"
            type={InputType.CHECKBOX}
            inputProps={{
              label: t('labelAgreeTermsAndConditions'),
            }}
          />
        </>
      )}

      {extraFields}
    </>
  );

  return (
    <Form
      title={t('titleCreateVendorQuote', { entityType })}
      isOpen={isOpen}
      onSubmit={onSubmit}
      onClose={onClose}
      cancelBtnLabel={t('buttonLabelCancel')}
      disabledSubmit={QuoteEntityType.ORDER === entityType && !watch('agreeTermsAndConditions')}
      inline={withoutForm}
      withoutButtons={withoutForm}
      withoutNotification={withoutNotification}
    >
      {fieldsStack}
    </Form>
  );
};
